import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { asActionSet, toAllowedActions } from './mappers';
import { fetchMyPermissions } from './transport';

import type { PermissionsQueryError, PermissionsScope } from './types';
import type { CatalogueAction } from 'modules/authorization/core';

/**
 * The cache entry a scope owns.
 *
 * No invalidation spends this precision. The listener dirties the whole `'PERMISSIONS'` type
 * instead, because at most two entries are live at a time and a targeted tag can miss - the
 * reasoning is in `listener.ts`. Kept rather than collapsed to a bare type because `providesTags`
 * has to name the entry either way, and naming it by scope is what makes the slice's `provided`
 * map readable.
 */
export const permissionsTagFor = (scope: PermissionsScope) =>
  scope.kind === 'community' ? `community:${scope.communityId}` : `organisation:${scope.organisationId}`;

/**
 * `permissions-service` on its own transport.
 *
 * `fakeBaseQuery` because the single endpoint supplies its own `queryFn` - see `transport.ts` for
 * why this cannot go through `axiosBaseQuery`. There is no shared `createApi` in this app, so the
 * reducer and the middleware are both registered by hand in `store/index.ts`; omitting the
 * middleware means the query silently never fires.
 *
 * No listener is wired for the error toast. U17 owns what an outage looks like to the user, and a
 * toast per failed permission check would fire once per gate.
 */
export const permissionsApi = createApi({
  reducerPath: 'permissionsApi',
  tagTypes: ['PERMISSIONS'],
  baseQuery: fakeBaseQuery<PermissionsQueryError>(),
  endpoints: (builder) => ({
    /**
     * The grants held in one scope, as a sorted, identity-stable array.
     *
     * An array rather than the `ReadonlySet` every reader wants, so the cache stays serialisable
     * and legible in Sentry and devtools - see `toAllowedActions`. Readers wrap it in
     * `asActionSet`, which is memoised on this array's identity and so costs one lookup.
     */
    getMyPermissions: builder.query<readonly CatalogueAction[], PermissionsScope>({
      providesTags: (_result, _error, scope) => [{ type: 'PERMISSIONS', id: permissionsTagFor(scope) }],
      queryFn: async (scope, api) => {
        const result = await fetchMyPermissions(scope, api.signal);

        return 'error' in result ? result : { data: toAllowedActions(result.data.allowed) };
      },
    }),
  }),
});

export const { useGetMyPermissionsQuery } = permissionsApi;

/** Re-exported beside the slice, since a reader of the grant list always wants both. */
export { asActionSet };
